/**
 * Salad Builder Component
 * Modern chip/pill-based topping selector with modal overlay
 */

import { useState } from "react";
import { useSaladToppings } from "../../hooks/useMenu";

interface SaladBuilderProps {
  targetDate?: string;
  onOrder?: (toppingIds: number[], toppingNames: string[]) => void;
  disabled?: boolean;
}

export function SaladBuilder({ onOrder, disabled = false, targetDate: _targetDate }: SaladBuilderProps) {
  const { toppings, loading, error } = useSaladToppings();
  const [selectedToppings, setSelectedToppings] = useState<Map<number, string>>(new Map());
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleToppingToggle = (id: number, name: string) => {
    if (disabled) return;

    setSelectedToppings(prev => {
      const newMap = new Map(prev);
      if (newMap.has(id)) {
        newMap.delete(id);
      } else {
        newMap.set(id, name);
      }
      return newMap;
    });
  };

  const handleAddToOrder = () => {
    if (selectedToppings.size === 0) {
      alert('Please select at least one topping');
      return;
    }
    if (onOrder && !disabled) {
      const ids = Array.from(selectedToppings.keys());
      const names = Array.from(selectedToppings.values());
      onOrder(ids, names);
      setIsModalOpen(false);
      setSelectedToppings(new Map()); // Reset selections after adding to order
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleOpenModal = () => {
    if (!disabled) {
      setIsModalOpen(true);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4">
          <h3 className="text-sm font-semibold text-gray-900">Toppings Unavailable</h3>
          <p className="text-gray-500 mt-1">Unable to load salad toppings</p>
        </div>
      </div>
    );
  }

  // Category color schemes for chips
  const categoryStyles: Record<string, { selected: string; unselected: string; header: string }> = {
    Protein: {
      selected: "bg-hmh-blue text-white shadow-md",
      unselected: "bg-white border-2 border-hmh-blue text-hmh-blue hover:bg-hmh-blue/10",
      header: "text-hmh-blue"
    },
    Vegetables: {
      selected: "bg-green-600 text-white shadow-md",
      unselected: "bg-white border-2 border-green-600 text-green-600 hover:bg-green-50",
      header: "text-green-600"
    },
    Cheese: {
      selected: "bg-amber-500 text-white shadow-md",
      unselected: "bg-white border-2 border-amber-500 text-amber-600 hover:bg-amber-50",
      header: "text-amber-600"
    },
    Crunchies: {
      selected: "bg-orange-600 text-white shadow-md",
      unselected: "bg-white border-2 border-orange-600 text-orange-600 hover:bg-orange-50",
      header: "text-orange-600"
    },
  };

  return (
    <>
      {/* Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col">
        <div className="bg-hmh-blue px-4 py-3">
          <h3 className="text-lg font-bold text-white">Salad</h3>
        </div>
        <div className="p-6 flex-grow flex flex-col">
          <div className="flex-grow">
            <p className="text-xl font-bold text-gray-900 mb-2">Build Your Own Salad</p>
            <p className="text-base text-gray-900 mb-4">
              Build your own custom salad with fresh toppings
            </p>
          </div>
          <button
            onClick={handleOpenModal}
            disabled={disabled}
            className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
              disabled
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-hmh-blue text-white hover:bg-hmh-dark'
            }`}
          >
            Build a Salad
          </button>
        </div>
      </div>

      {/* Modal Overlay */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={handleCancel}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-hmh-dark to-hmh-blue text-white px-6 py-4 flex items-center justify-between rounded-t-lg z-10">
              <div className="flex items-center gap-3">
                <svg
                  className="h-8 w-8"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                  />
                </svg>
                <div>
                  <h2 className="text-xl font-bold">Build Your Salad</h2>
                  <p className="text-sm text-white/80">
                    {selectedToppings.size > 0 ? `${selectedToppings.size} topping${selectedToppings.size !== 1 ? 's' : ''} selected` : 'Select your toppings'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleCancel}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                aria-label="Close"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <div className="space-y-6">
                {toppings?.categories.map((category) => {
                  const style = categoryStyles[category.name] || {
                    selected: "bg-hmh-blue text-white shadow-md",
                    unselected: "bg-white border-2 border-hmh-blue text-hmh-blue hover:bg-hmh-blue/10",
                    header: "text-hmh-blue"
                  };

                  return (
                    <div key={category.name} className="animate-fadeIn">
                      {/* Category Header */}
                      <div className="flex items-center gap-2 mb-3">
                        <h4 className={`text-sm font-bold uppercase tracking-wider ${style.header}`}>
                          {category.name}
                        </h4>
                        <div className="flex-1 h-0.5 bg-gray-200 rounded-full"></div>
                      </div>

                      {/* Chips Grid */}
                      <div className="flex flex-wrap gap-2">
                        {category.toppings.map((topping) => {
                          const isSelected = selectedToppings.has(topping.id);
                          return (
                            <button
                              key={topping.id}
                              onClick={() => handleToppingToggle(topping.id, topping.name)}
                              disabled={disabled}
                              className={`
                                group relative px-4 py-2.5 rounded-full font-medium text-sm
                                transition-all duration-200 ease-in-out
                                ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer active:scale-95'}
                                ${isSelected ? style.selected : style.unselected}
                                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hmh-blue
                              `}
                            >
                              {/* Checkmark Icon for Selected Items */}
                              {isSelected && (
                                <span className="inline-flex items-center mr-1.5">
                                  <svg
                                    className="w-4 h-4"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </span>
                              )}
                              <span>{topping.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Modal Footer - Action Buttons */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3 rounded-b-lg">
              <button
                onClick={handleCancel}
                className="px-6 py-2.5 rounded-lg font-medium text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddToOrder}
                disabled={disabled || selectedToppings.size === 0}
                className={`
                  px-6 py-2.5 rounded-lg font-medium text-sm
                  transition-all duration-200 ease-in-out
                  ${
                    disabled || selectedToppings.size === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-hmh-dark to-hmh-blue text-white hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]'
                  }
                `}
              >
                {selectedToppings.size === 0 ? 'Select Toppings' : 'Add to Order'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
